import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';

import { shouldShowSupplements } from 'dux/featureFlags/selectors';
import ConsultationCTA from 'Containers/ConsultationCTA';
import Typography from 'Components/Typography';

import content from 'assets/content/footer';
import company from 'assets/content/company';

const shadow = '0 10px 14px -10px rgba(0, 0, 0, 0.04)';

const styles = makeStyles(theme => ({
  root: ({ paddedOnRightOnDesktop }) => ({
    color: theme.palette.common.white.light,
    background: theme.palette.common.noir.dark,
    width: '100%',
    display: 'flex',
    boxShadow: shadow,
    flexDirection: 'row',
    [theme.breakpoints.match('md')]: {
      flexDirection: 'column',
    },
    ...(paddedOnRightOnDesktop && {
      paddingRight: '450px',
      [theme.breakpoints.match('md')]: {
        paddingRight: '0px',
      },
    }),
  }),
  cta: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 224,
    color: '#fff',
    background: theme.palette.common.noir.medium,
    flex: 1,
    order: 2,
    [theme.breakpoints.match('md')]: {
      order: 1,
    },
  },
  grid: ({ showSupplements }) => ({
    display: 'grid',
    justifyContent: 'space-around',
    justifyItems: showSupplements ? 'start' : 'center',
    alignItems: 'flex-start',
    gridTemplateAreas: showSupplements
      ? `
    "contactUs      faq         partner       bCorp     "
    "careers        stylists    terms         bCorp     "
    "blog           privacy     accessibility bCorp     "
    "social         social      social        bCorp     "
    "copywright     copywright  supplements   supplements     "
    "sitemap        sitemap     supplements   supplements     "
    "donotsell      donotsell   supplements   supplements     "
    `
      : `
    "contactUs      faq         partner       bCorp     "
    "careers        stylists    terms         bCorp     "
    "blog           privacy     accessibility bCorp     "
    "social         social      social        bCorp     "
    "copywright     copywright  copywright    bCorp     "
    "sitemap        sitemap     sitemap       bCorp     "
    "donotsell      donotsell   donotsell     bCorp     "
    `,
    flex: 2,
    padding: showSupplements
      ? `${theme.props.verticalSpacingDes}px 0px ${theme.props.verticalSpacingDes}px 50px`
      : `${theme.props.verticalSpacingDes}px 0px`,
    order: 1,
    [theme.breakpoints.match('md')]: {
      order: 2,
      padding: `48px ${theme.props.verticalSpacingMob}px 70px`,
      gridTemplateColumns: '1fr 1fr 1fr',
      gridTemplateAreas: ` 
        "contactUs    careers     blog"
        "faq          stylists    partner"
        "privacy      terms       accessibility"
        "border       border      border"
        "bCorp        bCorp       bCorp"
        "social       social      social"
        "copywright   copywright  copywright"
        "sitemap      sitemap     sitemap"
        "donotsell    donotsell   donotsell"
        "supplements  supplements supplements"
      `,
      justifyItems: 'center',
      flex: 1,
      textAlign: 'center',
      '& a': {
        marginBottom: '30px',
      },
    },
    [theme.breakpoints.match('sm')]: {
      padding: '80px 24px 62px',
      gridTemplateColumns: '1fr 1fr',
      gridTemplateAreas: ` 
        "contactUs      blog      " 
        "careers        faq       "
        "stylists       privacy   "
        "partner        terms     "
        "accessibility  null      "
        "border         border    "
        "bCorp          bCorp     "
        "social         social    "
        "copywright     copywright"
        "sitemap        sitemap   "
        "donotsell      donotsell "
        "supplements  supplements"
      `,
    },
  }),
  accessibility: {
    gridArea: 'accessibility',
  },
  contactUs: {
    gridArea: 'contactUs',
  },
  careers: {
    gridArea: 'careers',
  },
  blog: {
    gridArea: 'blog',
  },
  faq: {
    gridArea: 'faq',
  },
  social: {
    gridArea: 'social',
  },
  terms: {
    gridArea: 'terms',
  },
  privacy: ({ showSupplements }) => ({
    gridArea: 'privacy',
    display: 'flex',
    flexDirection: 'column',
    alignItems: showSupplements ? 'start' : 'center',
    '& > a': {
      marginBottom: 0,
    },
    [theme.breakpoints.match('md')]: {
      alignItems: 'center',
    },
  }),
  partnerProgram: {
    gridArea: 'partner',
  },
  stylists: {
    gridArea: 'stylists',
  },
  link: {
    ...theme.typography.p2,
    margin: `${theme.spacing.s8}px 0`,
    color: theme.palette.common.white.light,
    [theme.breakpoints.match('md')]: {
      margin: `${theme.spacing.s8}px`,
    },
  },
  border: {
    gridArea: 'border',
    border: 'none',
    borderTop: '1px solid rgba(255, 255, 255, 0.15)',
    width: '100%',
    margin: `${theme.spacing.s16}px  0`,
    display: 'none',
    [theme.breakpoints.match('md')]: {
      display: 'block',
    },
  },
  bCorp: {
    gridArea: 'bCorp',
    width: 68,
    height: 100,
    [theme.breakpoints.match('md')]: {
      margin: theme.spacing.s24,
    },
  },
  copywright: {
    gridArea: 'copywright',
    color: theme.palette.common.grey.medium,
    marginBottom: theme.spacing.s8,
  },
  sitemap: {
    gridArea: 'sitemap',
    color: theme.palette.common.grey.medium,
    marginBottom: theme.spacing.s8,
  },
  donotsell: ({ showSupplements }) => ({
    gridArea: 'donotsell',
    marginBottom: theme.spacing.s36,
    color: theme.palette.common.grey.medium,
    ...(showSupplements && {
      [theme.breakpoints.up('sm')]: {
        marginBottom: theme.spacing.s36,
      },
      [theme.breakpoints.up('md')]: {
        marginBottom: 0,
      },
    }),
  }),
  socialLinkImg: ({ showSupplements }) => ({
    width: 20,
    height: 20,
    margin: showSupplements
      ? `${theme.spacing.s72}px ${theme.spacing.s32}px ${theme.spacing.s16}px 0px`
      : `${theme.spacing.s72}px ${theme.spacing.s16}px ${theme.spacing.s16}px`,
    [theme.breakpoints.match('md')]: {
      margin: theme.spacing.s16,
    },
  }),
  supplements: {
    gridArea: 'supplements',
    maxWidth: '400px',
    margin: `0 34px ${theme.spacing.s40}px 34px`,
    padding: '20px 18px',
    border: `1px solid ${theme.palette.common.noir.medium}`,
    textAlign: 'left',
    lineHeight: 1.5,
    letterSpacing: '0.5px',
    fontSize: '12px',
    color: theme.palette.common.white.medium,
    [theme.breakpoints.up('sm')]: {
      marginBottom: '24px',
      padding: `21px ${theme.spacing.s24}px`,
    },
    [theme.breakpoints.up('md')]: {
      marginBottom: '0',
      padding: `15px 20px`,
    },
    [theme.breakpoints.match('md')]: {
      textAlign: 'center',
      maxWidth: '344px',
      margin: `0 16px ${theme.spacing.s40}px 16px`,
      padding: '20px 14px',
    },
  },
}));

const currentYear = new Date().getFullYear();

const Footer = ({ hideCTA, paddedOnRightOnDesktop, showSupplements }) => {
  const classes = styles({ showSupplements, paddedOnRightOnDesktop });

  return (
    <footer className={classes.root} data-testid="footer" id="footer">
      {!hideCTA && (
        <div className={classes.cta} data-testid="footerCta">
          <Typography align="center" paragraph variant="h3">
            {content.message}
          </Typography>
          <ConsultationCTA dataFrom="footer" theme="whiteBg" />
        </div>
      )}
      <div className={classes.grid}>
        <Link
          className={classNames(classes.link, classes.contactUs)}
          data-click="contact-us"
          data-from="footer"
          to="/contact"
        >
          Contact us
        </Link>
        <Link
          className={classNames(classes.link, classes.careers)}
          data-click="careers"
          data-from="footer"
          to="/careers"
        >
          Careers
        </Link>
        <a
          className={classNames(classes.link, classes.blog)}
          data-click="blog"
          data-from="footer"
          href="/blog/"
        >
          Blog
        </a>
        <Link
          className={classNames(classes.link, classes.faq)}
          data-click="faq"
          data-from="footer"
          to="/faq"
        >
          Help + FAQ
        </Link>
        <Link
          className={classNames(classes.link, classes.stylists)}
          data-click="for-stylists"
          data-from="footer"
          to="/pages/stylist-content-call"
        >
          For stylists
        </Link>
        <Link
          className={classNames(classes.link, classes.terms)}
          data-click="terms"
          data-from="footer"
          to="/terms"
        >
          Terms of use
        </Link>
        <Link
          className={classNames(classes.link, classes.accessibility)}
          data-click="accessibility"
          data-from="footer"
          to="/accessibility"
        >
          Accessibility
        </Link>
        <div className={classes.privacy}>
          <Link
            className={classNames(classes.link)}
            data-click="privacy"
            data-from="footer"
            to="/privacy"
          >
            Privacy policy
          </Link>
          <Typography color="sorbet" variant="mono4">
            updated
          </Typography>
        </div>
        <a
          className={classNames(classes.link, classes.partnerProgram)}
          data-click="partner-program"
          data-from="footer"
          href="https://app.impact.com/campaign-campaign-info-v2/Prose.brand "
          rel="noopener noreferrer"
          target="_blank"
        >
          Partner program
        </a>
        <div className={classes.social}>
          {company.social.map(social => (
            <a
              key={social.name}
              data-click={social.name}
              data-from="footer"
              href={social.url}
              rel="noopener noreferrer"
              target="_blank"
            >
              <img
                alt={`${social.name} icon - vector`}
                className={classes.socialLinkImg}
                src={social.img}
              />
            </a>
          ))}
        </div>

        <hr className={classes.border} />

        <Link
          className={classes.bCorp}
          data-click="bcorp"
          data-from="footer"
          to="/values-and-impact"
        >
          <img alt={`${content.bCorp.alt} logo - vector`} src={content.bCorp.image} />
        </Link>

        <Typography className={classes.copywright} variant="mono3">
          &copy; {currentYear} {company.legalName}
        </Typography>
        <Typography
          className={classes.sitemap}
          data-click="sitemap"
          data-from="footer"
          markupName={Link}
          to="/sitemap"
          variant="mono3"
        >
          Site map
        </Typography>
        <a className={classes.donotsell} href={content.doNotSell.link}>
          <Typography variant="mono3">{content.doNotSell.copy}</Typography>
        </a>

        {showSupplements && (
          <Typography className={classes.supplements} markupName="p" variant="p3">
            *These statements have not been evaluated by the Food and Drug Administration. This
            product is not intended to diagnose, treat, cure or prevent any disease.
          </Typography>
        )}
      </div>
    </footer>
  );
};

Footer.defaultProps = {
  hideCTA: false,
  paddedOnRightOnDesktop: false,
};

Footer.propTypes = {
  hideCTA: PropTypes.bool,
  paddedOnRightOnDesktop: PropTypes.bool,
  showSupplements: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  showSupplements: shouldShowSupplements(state),
});

export default connect(mapStateToProps)(Footer);
